import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ShieldCheck,
  DollarSign,
  TrendingDown,
  Phone,
  Mail,
  Upload,
  ArrowUpRight,
  ChevronRight,
  Sparkles,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, StatCard, EmptyState, Skeleton } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { Avatar } from '@/components/ui/avatar';
import { CsvImportModal } from '@/components/csv-import-modal';
import { DemoDataBanner } from '@/components/demo-data-banner';
import { useImportModalFromSearch } from '@/lib/import-search';
import { cn, api, formatCurrency } from '@/lib/utils';

type RiskLevel = 'low' | 'medium' | 'high';

interface RiskItem {
  id: string;
  contactId: string;
  contactName: string;
  contactEmail: string | null;
  contactPhone: string | null;
  planName: string;
  amount: number;
  endDate: string | null;
  relativeEndDate: string;
  displayStatus: string;
  risk: {
    score: number;
    level: RiskLevel;
    reasons: string[];
    churnProbability: number;
    upgradeOpportunity: boolean;
  };
}

interface RiskResponse {
  board: RiskItem[];
  summary: {
    activeAgreements: number;
    expectedRenewalRevenue: number;
    revenueAtRisk: number;
    likelyChurnPercent: number;
    totalAnnualValue: number;
    counts: { low: number; medium: number; high: number };
    upgradeOpportunities: number;
  };
  fullForecast?: boolean;
  modelVersion?: number;
}

interface CohortResponse {
  horizonDays: number;
  customerCount: number;
  revenueAtRisk: number;
  items: RiskItem[];
}

interface TrendResponse {
  trend: Array<{
    snapshotDate: string;
    revenueAtRisk: number;
    predictedLapses90d: number;
  }>;
}

interface WinbackStats {
  lapsedCount: number;
  enrolledCount: number;
  recoveredCount: number;
  recoveredRevenue: number;
  recoveryRate: number;
}

const HORIZONS = [30, 60, 90] as const;

const RISK_STYLES: Record<RiskLevel, { badge: string; dot: string; label: string }> = {
  high: { badge: 'bg-[#FEE2E2] text-[#991B1B]', dot: 'bg-[#DC2626]', label: 'High risk' },
  medium: { badge: 'bg-[#FEF9C3] text-[#854D0E]', dot: 'bg-[#CA8A04]', label: 'Medium risk' },
  low: { badge: 'bg-[#DCFCE7] text-[#166534]', dot: 'bg-[#16A34A]', label: 'Low risk' },
};

function RiskBadge({ level }: { level: RiskLevel }) {
  const s = RISK_STYLES[level];
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium', s.badge)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', s.dot)} />
      {s.label}
    </span>
  );
}

function RiskTrendChart({ points }: { points: TrendResponse['trend'] }) {
  if (points.length < 2) {
    return (
      <p className="text-sm text-text-muted">
        Trend data builds nightly — check back after a few days of activity.
      </p>
    );
  }

  const width = 320;
  const height = 80;
  const max = Math.max(...points.map((p) => p.revenueAtRisk), 1);
  const coords = points.map((p, i) => {
    const x = (i / (points.length - 1)) * width;
    const y = height - (p.revenueAtRisk / max) * (height - 8) - 4;
    return `${x},${y}`;
  });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-20 w-full max-w-md" aria-hidden>
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="text-danger"
        points={coords.join(' ')}
      />
    </svg>
  );
}

export function RetentionPage() {
  const qc = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  useImportModalFromSearch(setImportOpen);
  const [horizon, setHorizon] = useState<(typeof HORIZONS)[number]>(90);
  const [cohortFilter, setCohortFilter] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['risk'],
    queryFn: () => api<RiskResponse>('/api/v1/risk'),
  });

  const fullForecast = data?.fullForecast ?? false;

  const { data: cohort, isLoading: cohortLoading } = useQuery({
    queryKey: ['risk-cohort', horizon],
    queryFn: () => api<CohortResponse>(`/api/v1/risk/cohort?horizon=${horizon}`),
    enabled: fullForecast,
  });

  const { data: trendData } = useQuery({
    queryKey: ['risk-trend'],
    queryFn: () => api<TrendResponse>('/api/v1/risk/trend?days=180'),
    enabled: fullForecast,
  });

  const { data: winbackStats, isLoading: winbackLoading } = useQuery({
    queryKey: ['winback-stats'],
    queryFn: () => api<WinbackStats>('/api/v1/winback/stats'),
    enabled: fullForecast,
  });

  const worklist = useMemo(() => {
    if (fullForecast && cohortFilter && cohort?.items) return cohort.items;
    if (fullForecast && cohort?.items?.length) return cohort.items;
    return (data?.board ?? []).filter((i) => i.risk.level !== 'low');
  }, [data?.board, cohort?.items, cohortFilter, fullForecast]);

  const cohortCount = cohort?.customerCount ?? 0;
  const cohortRevenue = cohort?.revenueAtRisk ?? 0;

  return (
    <div className="space-y-6">
      <DemoDataBanner />
      <PageHeader
        title="At risk"
        description="See who won't renew, why, and who to call first — before the agreement revenue walks."
        actions={
          <Button variant="secondary" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" /> Import from spreadsheet
          </Button>
        }
      />

      {fullForecast && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium text-text-secondary">Forecast window</span>
          {HORIZONS.map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => {
                setHorizon(h);
                setCohortFilter(true);
              }}
              className={cn(
                'rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors',
                horizon === h
                  ? 'border-brand bg-brand-subtle text-brand'
                  : 'border-border text-text-secondary hover:border-brand/40',
              )}
            >
              {h} days
            </button>
          ))}
        </div>
      )}

      {fullForecast && (
        <button
          type="button"
          onClick={() => setCohortFilter(true)}
          className="w-full rounded-2xl border border-danger/20 bg-danger-subtle/40 p-5 text-left transition-colors hover:border-danger/40"
        >
          {cohortLoading ? (
            <Skeleton className="h-10 w-64" />
          ) : (
            <>
              <p className="text-sm font-medium text-[#991B1B]">Likely to lapse in the next {horizon} days</p>
              <p className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
                <Users className="mr-2 inline h-6 w-6 text-danger" />
                {cohortCount} {cohortCount === 1 ? 'customer' : 'customers'}
                <span className="mx-2 text-text-muted">·</span>
                {formatCurrency(cohortRevenue)} at risk
              </p>
              <p className="mt-1 text-[13px] text-text-secondary">
                Renewals due in this window with elevated lapse risk — tap to focus the worklist below.
              </p>
            </>
          )}
        </button>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Active agreements"
          value={data?.summary.activeAgreements ?? 0}
          icon={ShieldCheck}
          tone="default"
          loading={isLoading}
          to="/agreements"
          search={{ tab: 'active' }}
        />
        <StatCard
          label="Expected renewal revenue"
          value={formatCurrency(data?.summary.expectedRenewalRevenue ?? 0)}
          icon={DollarSign}
          tone="success"
          hint="After at-risk renewals"
          loading={isLoading}
          to="/agreements"
          search={{ tab: 'due_soon' }}
        />
        <StatCard
          label="Revenue at risk"
          value={formatCurrency(data?.summary.revenueAtRisk ?? 0)}
          icon={TrendingDown}
          tone="danger"
          hint="If you do nothing"
          loading={isLoading}
        />
        <StatCard
          label="At risk of lapsing"
          value={`${data?.summary.likelyChurnPercent ?? 0}%`}
          icon={TrendingDown}
          tone="warning"
          hint="Share of active book"
          loading={isLoading}
        />
      </div>

      {fullForecast && (
        <Card className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Lapsed customers</h2>
              <p className="mt-1 text-sm text-text-secondary">
                Win-back campaigns work agreements after the grace window and track recovered revenue.
              </p>
            </div>
            <Link to="/campaigns" className="text-sm font-medium text-brand hover:underline">
              Manage win-back sequence
            </Link>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <StatCard
              label="Currently lapsed"
              value={winbackStats?.lapsedCount ?? 0}
              icon={Users}
              tone="warning"
              loading={winbackLoading}
              to="/agreements"
              search={{ tab: 'overdue' }}
            />
            <StatCard
              label="In win-back"
              value={winbackStats?.enrolledCount ?? 0}
              icon={Mail}
              tone="default"
              loading={winbackLoading}
              to="/campaigns"
            />
            <StatCard
              label="Recovered revenue"
              value={formatCurrency(winbackStats?.recoveredRevenue ?? 0)}
              icon={DollarSign}
              tone="success"
              hint={
                winbackStats?.enrolledCount
                  ? `${winbackStats.recoveryRate}% recovery rate`
                  : 'From win-back payments'
              }
              loading={winbackLoading}
              to="/campaigns"
            />
          </div>
        </Card>
      )}

      {fullForecast && (
        <Card title="Revenue at risk over time" description="Daily snapshot — are you winning or losing ground?">
          <RiskTrendChart points={trendData?.trend ?? []} />
        </Card>
      )}

      <Card
        title="Call this week"
        description={
          fullForecast
            ? `Customers renewing in the next ${horizon} days who are most likely to lapse. Each one shows exactly why.`
            : 'Customers most likely to lapse, highest risk first. Each one shows exactly why.'
        }
        noPadding
        action={
          worklist.length > 0 ? (
            <span className="text-[13px] font-medium text-text-secondary">
              {worklist.length} {worklist.length === 1 ? 'customer' : 'customers'} flagged
            </span>
          ) : undefined
        }
      >
        {isLoading || (fullForecast && cohortLoading) ? (
          <div className="divide-y divide-border">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-4">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-44" />
                  <Skeleton className="h-3 w-64" />
                </div>
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
            ))}
          </div>
        ) : worklist.length ? (
          <div className="divide-y divide-border">
            {worklist.map((item) => (
              <WorklistRow key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={ShieldCheck}
            title="No one's at risk right now"
            description="Every active agreement looks healthy. Import your customers or add agreements to keep this list sharp."
            action={
              <Button variant="secondary" onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4" /> Import from spreadsheet
              </Button>
            }
          />
        )}
      </Card>

      {(data?.summary.upgradeOpportunities ?? 0) > 0 && (
        <Link to="/upgrades">
          <Card className="transition-colors hover:border-brand/30">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-brand">
                <ArrowUpRight className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold">{data!.summary.upgradeOpportunities} equipment leads</p>
                <p className="text-[13px] text-text-secondary">
                  Customers with equipment 10+ years old — good candidates for a replacement conversation.
                </p>
              </div>
              <ArrowUpRight className="h-4 w-4 text-text-muted" />
            </div>
          </Card>
        </Link>
      )}

      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['risk'] });
          qc.invalidateQueries({ queryKey: ['customers'] });
          qc.invalidateQueries({ queryKey: ['agreements'] });
        }}
        defaultKind="agreements"
      />
    </div>
  );
}

function WorklistRow({ item }: { item: RiskItem }) {
  return (
    <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
      <Avatar name={item.contactName} size="md" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold">{item.contactName}</p>
          <RiskBadge level={item.risk.level} />
          {item.risk.upgradeOpportunity && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-subtle px-2 py-0.5 text-[11px] font-medium text-brand">
              <Sparkles className="h-3 w-3" /> Replacement
            </span>
          )}
        </div>
        <p className="mt-0.5 truncate text-[13px] text-text-secondary">
          {item.planName} · {formatCurrency(item.amount)}/yr · renews {item.relativeEndDate}
        </p>
        {item.risk.reasons.length > 0 && (
          <p className="mt-1 text-xs text-text-muted">{item.risk.reasons.join(' · ')}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {item.contactPhone && (
          <a href={`tel:${item.contactPhone}`} title={`Call ${item.contactPhone}`}>
            <Button variant="secondary" size="sm">
              <Phone className="h-3.5 w-3.5" /> Call
            </Button>
          </a>
        )}
        {item.contactEmail && (
          <a href={`mailto:${item.contactEmail}`} title={`Email ${item.contactEmail}`}>
            <Button variant="ghost" size="sm">
              <Mail className="h-3.5 w-3.5" />
            </Button>
          </a>
        )}
        <Link to="/agreements" search={{ selected: item.id } as never}>
          <Button variant="ghost" size="sm">
            Open <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
